import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as BackArrowIcon } from '../../../assets/icons/arrow-left.svg';
// import { ReactComponent as Calender } from '../../../assets/icons/calender.svg';
import { ReactComponent as CloseCircleIcon } from '../../../assets/icons/close-circle.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
import { ReactComponent as EditPencilCard } from '../../../assets/icons/edit-pencil.svg';
import { ReactComponent as DotCircle } from '../../../assets/icons/tables/dot-circle.svg';
import { UserInterFace, UserParameters } from '../../../interface/users-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { fetchAllListRoles } from '../../../redux/slices/RolesManagementSlice';
import { fetchAllUsers, fetchUserActivityLogs } from '../../../redux/slices/UsersManagementSlice';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomPhoneInput from '../../../shared-components/custom-input/CustomPhoneInput';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SelectFiled from '../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import { changeDateFormat } from '../../../shared-functions/DateTime';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import {
  emailValidation,
  phoneNumberValidation,
  stringValidation,
} from '../../../shared-functions/Validation';
import userApi from '../../../utils/api/users';

import DeleteUser from './delete-user';

interface UserDetailsSidePanelProperties {
  fetchIdFrom?: 'queryParams' | 'props';
  id?: string;
  isEditMode?: boolean;
  onEditModeChange?: (state: boolean) => void;
  onClose?: () => void;
  showHeader?: boolean;
  completedTask?: (message: string) => void;
}

/**
 * User details side panel component
 * @param {UserDetailsSidePanelProperties} properties - Component properties
 * @returns {React.JSX.Element} User details side panel component
 */
function UserDetailsSidePanel(properties: UserDetailsSidePanelProperties) {
  const { t, i18n } = useTranslation('userRole');
  const dispatch = useAppDispatch();
  const {
    onEditModeChange,
    fetchIdFrom,
    id,
    onClose,
    showHeader,
    completedTask: parentCompletedTask,
  } = properties;
  const [searchParameters, setSearchParameters] = useSearchParams();
  const { users, userActiveLogs, loggingLoading, page, limit } = useAppSelector(
    (state) => state.users,
  );
  const { rolesList } = useAppSelector((state) => state.roles);

  const [userData, setUserData] = useState<UserInterFace | null>(null);

  // Modals state
  const [modalView, setModalView] = useState({
    status: false,
    title: '',
    content: <div />,
    header: false,
    rightCorner: '',
    notification: false,
  });

  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState<any>({});
  const [loading, setLoading] = useState(false);

  // Initial load
  useEffect(() => {
    let userId = id;
    if (fetchIdFrom === 'queryParams') {
      userId = searchParameters.get('userId') || undefined;
    }

    if (userId) {
      const found = users.find((u: UserInterFace) => u.id === userId || u._id === userId);
      if (found) {
        setUserData(found);
        // Fetch logs
        dispatch(fetchUserActivityLogs(userId));
        // Reset edit mode when user changes
        setEditMode(false);
        if (onEditModeChange) onEditModeChange(false);
      }
    }
  }, [fetchIdFrom, id, searchParameters, users]);

  // Sync editMode with search parameters when header is handled externally
  useEffect(() => {
    if (showHeader === false) {
      const isUrlEdit = searchParameters.get('action') === 'edit';
      if (isUrlEdit && !editMode) {
        if (userData) {
          setEditData({
            name: userData?.name || '',
            email: userData?.email || '',
            phoneNumber: userData?.phoneNumber || '',
            countryCode: userData?.countryCode || '',
            role: userData?.role || '',
            roleName: userData?.roleName || userData?.role || '',
            status: (userData?.isActive ?? userData?.status) ? 'Active' : 'Inactive',
          });
        }
        setEditMode(true);
      } else if (!isUrlEdit && editMode) {
        setEditMode(false);
        setEditData({});
      }
    }
  }, [searchParameters, showHeader, userData, editMode]);

  // Fetch roles
  useEffect(() => {
    if (rolesList.length === 0) {
      dispatch(fetchAllListRoles());
    }
  }, []);

  /**
   * Handle Close Side Panel
   */
  const handleClose = () => {
    const parameter = new URLSearchParams(searchParameters);
    parameter.delete('userId');
    parameter.delete('action');
    setSearchParameters(parameter);
    if (onClose) {
      onClose();
    }
  };

  /**
   * Toggle Edit Mode
   * @returns {void}
   */
  const toggleEditMode = () => {
    if (!permissionCheck('userManagementUpdate')) return;
    if (showHeader === false) {
      const newParameters = new URLSearchParams(searchParameters);
      if (searchParameters.get('action') === 'edit') {
        newParameters.delete('action');
      } else {
        newParameters.set('action', 'edit');
      }
      setSearchParameters(newParameters);
    } else if (editMode) {
      // Cancelling edit mode - also clean URL if needed
      const parameters = new URLSearchParams(searchParameters);
      if (parameters.get('action') === 'edit') {
        parameters.delete('action');
        setSearchParameters(parameters, { replace: true });
      }
      setEditMode(false);
      setEditData({});
      if (onEditModeChange) onEditModeChange(false);
    } else {
      // Entering edit mode
      setEditData({
        name: userData?.name || '',
        email: userData?.email || '',
        phoneNumber: userData?.phoneNumber || '',
        countryCode: userData?.countryCode || '',
        role: userData?.role || '',
        roleName: userData?.roleName || userData?.role || '',
        status: (userData?.isActive ?? userData?.status) ? 'Active' : 'Inactive',
      });
      setEditMode(true);
      if (onEditModeChange) onEditModeChange(true);
    }
  };

  /**
   * Handle Input Change
   * @param {string} name - Input name
   * @param {any} value - Input value
   */
  const handleInputChange = (name: string, value: string | number | (string | number)[]) => {
    setEditData((previous: any) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Handle Input Change
   */
  const completedTask = (message: string) => {
    if (parentCompletedTask) {
      parentCompletedTask(message);
    } else {
      dispatch(fetchAllUsers({ page, limit })); // Refresh list
      setModalView({
        status: true,
        title: '',
        header: false,
        rightCorner: '',
        notification: true,
        content: (
          <SuccessNotification
            title={message}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setModalView((previous) => ({ ...previous, status: false }));
            }}
          />
        ),
      });
    }
  };

  /**
   * Handle Edit Submit
   * @returns {void}
   */
  const handleEditSubmit = () => {
    if (!userData) return;

    if (
      editData.role === '' ||
      !stringValidation(1, 100, editData.name) ||
      editData.email === '' ||
      editData.phoneNumber === '' ||
      editData.countryCode === '' ||
      !emailValidation(editData.email) ||
      !phoneNumberValidation(editData.phoneNumber)
    ) {
      dispatch(
        setNotificationMessage({
          message: t('pleaseFillRequiredFields'),
          status: true,
          type: 'error',
          code: 0,
        }),
      );
      return;
    }

    setLoading(true);
    const request: UserParameters = {
      name: editData.name,
      roles: [editData.role],
      email: editData.email.toLowerCase(),
      phoneNumber: editData.phoneNumber,
      countryCode: editData.countryCode,
    };

    userApi
      .editUser(userData.id, request)
      .then(() => {
        setLoading(false);
        completedTask(t('userUpdatedSuccessfully'));
        // Remove edit mode URL param and reset state
        const parameters = new URLSearchParams(searchParameters);
        parameters.delete('action');
        setSearchParameters(parameters, { replace: true });
        setEditMode(false);
        setEditData({});
        onEditModeChange?.(false);
      })
      .catch((error) => {
        setLoading(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Delete user confirm function
   * @param {string} accountId - User ID to delete
   * @returns {void}
   */
  const deleteConfirmFunction = (accountId: string) => {
    console.log('Deleting User:', accountId);
    userApi
      .deleteUser(accountId)
      .then(() => {
        dispatch(fetchAllUsers({ page, limit }));
        setModalView((previous) => ({ ...previous, status: false }));
        handleClose();
        completedTask(t('userDeleted'));
      })
      .catch((error) => {
        setModalView((previous) => ({ ...previous, status: false }));
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle Delete Click
   */
  const handleDeleteClick = () => {
    console.log('Delete icon clicked');
    if (!permissionCheck('userManagementDelete')) return;
    if (!userData) return;
    setModalView({
      status: true,
      title: '',
      header: false,
      content: (
        <DeleteUser
          title={t('areYouSureDelete')}
          subTitle={t('deleteWarning')}
          closeModal={() => setModalView((previous) => ({ ...previous, status: false }))}
          submitButton={
            <MyButton
              label={t('delete')}
              buttonType="error"
              onClickFunc={() => deleteConfirmFunction(userData.id)}
              svgIcon={<DeleteIcon />}
            />
          }
        />
      ),
      rightCorner: '',
      notification: false,
    });
  };

  if (!userData) return <div className="no-data">{t('noDataFound')}</div>;

  return (
    <div className="user-details-side-panel">
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setModalView((previous) => ({ ...previous, status: false }))}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />

      {showHeader !== false && (
        <div className="panel-header">
          {editMode && (
            <div className="edit-close">
              <BackArrowIcon
                onClick={toggleEditMode}
                style={{
                  transform: i18n.dir() === 'rtl' ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </div>
          )}
          <h3>{editMode ? t('editUser') : t('userDetails')}</h3>
          <div className="header-actions">
            {!editMode && (
              <>
                <div className="edit-delete">
                  {permissionCheck('userManagementUpdate') ? (
                    <EditPencilCard onClick={toggleEditMode} />
                  ) : (
                    <EditPencilCard className="disabled-icon" />
                  )}
                  {permissionCheck('userManagementDelete') ? (
                    <button
                      type="button"
                      onClick={handleDeleteClick}
                      className="icon-button"
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                      <DeleteIcon style={{ cursor: 'pointer' }} />
                    </button>
                  ) : (
                    <DeleteIcon className="disabled-icon" />
                  )}
                </div>
                <div className="divider-vertical" />
              </>
            )}
            <CloseCircleIcon className="close-btn" onClick={handleClose} />
          </div>
        </div>
      )}

      <div
        style={{
          overflowY: 'auto',
          height: 'calc(100vh - 150px)',
        }}>
        <div className="user-details-group">
          <div className="profile-card">
            {editMode ? (
              // Edit Mode Fields
              <div className="edit-fields-container">
                <div className="profile-info">
                  <div className="name">{userData?.name || '-'}</div>
                  <div className="role">{userData?.roleName || userData?.role || 'Manager'}</div>
                  <div className="divider" />
                  <div className="detail-row">
                    <div className="label">{t('createdAt')}:</div>
                    <div className="value" style={{ fontWeight: 'bold' }}>
                      {userData?.createdAt ? changeDateFormat(userData?.createdAt) : '-'}
                    </div>
                  </div>
                  <div style={{ marginTop: '20px' }} />

                  <div className="info-row-input">
                    <CustomInput
                      type="text"
                      name="name"
                      placeholder="User Name"
                      value={editData.name}
                      onChangeValue={handleInputChange}
                      inputLabel={t('userName')}
                    />
                    <SelectFiled
                      name="role"
                      inputLabel={t('role')}
                      selectedValue={editData.role}
                      placeholder={t('selectRole')}
                      onChangeValue={handleInputChange}
                      options={rolesList}
                    />
                  </div>

                  <div className="info-row-input">
                    <CustomInput
                      type="email"
                      name="email"
                      placeholder="Email"
                      value={editData.email}
                      onChangeValue={handleInputChange}
                      inputLabel={t('email')}
                    />
                    <CustomPhoneInput
                      type="number"
                      name="phoneNumber"
                      placeholder="Phone Number"
                      value={editData.phoneNumber}
                      value1={editData.countryCode}
                      onChangeValue={handleInputChange}
                      inputLabel={t('phoneNumber')}
                    />
                  </div>

                  <div className="info-row-input">
                    <div>
                      <p className="label" style={{ marginBottom: '5px' }}>
                        {t('status')}:
                      </p>
                      <div
                        className="tag-success-label"
                        style={{ padding: '5px 12px', borderRadius: '15px', width: 'fit-content' }}>
                        <DotCircle /> <span style={{ marginLeft: '5px' }}>{t('active')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // View Mode Layout
              <div className="profile-info">
                <div className="name">{userData?.name || '-'}</div>
                <div className="role">{userData?.roleName || userData?.role || 'Manager'}</div>

                <div className="divider" />

                <div className="detail-row">
                  <div className="label">{t('createdAt')}:</div>
                  <div className="value" style={{ fontWeight: 'bold' }}>
                    {userData?.createdAt ? changeDateFormat(userData?.createdAt) : '-'}
                  </div>
                </div>

                <div className="detail-row">
                  <div className="label">{t('contact')}:</div>
                  <div className="value" style={{ fontWeight: 'bold' }}>
                    {userData?.email || '-'}
                  </div>
                </div>

                <div className="detail-row">
                  <div className="label">{t('status')}:</div>
                  {(userData.isActive ?? userData.status) ? (
                    <div
                      className="tag-success-label"
                      style={{ borderRadius: '15px', padding: '2px 10px' }}>
                      <DotCircle /> <span>{t('active')}</span>
                    </div>
                  ) : (
                    <div
                      className="tag-danger-label"
                      style={{ borderRadius: '15px', padding: '2px 10px' }}>
                      <DotCircle /> <span>{t('inactive')}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {!editMode && (
            <div className="section activity-log-section">
              <div className="activity-header">
                <h4>{t('activityLog')}</h4>
                {/* <div className="date-picker-trigger">
                                    DD/MM/YYYY - DD/MM/YYYY <Calender />
                                </div> */}
              </div>

              <div className="timeline-list">
                {loggingLoading && (
                  <div className="not-found" style={{ display: 'flex', justifyContent: 'center' }}>
                    <BatteryLoader />
                  </div>
                )}

                {!loggingLoading &&
                  userActiveLogs.length > 0 &&
                  userActiveLogs.map((activityLog, index) => (
                    <React.Fragment key={index}>
                      {activityLog?.logs?.map((log, logIndex) => (
                        <div className="timeline-item" key={logIndex}>
                          <div className="timeline-marker">
                            <div className="dot" />
                            <div className="line" />
                          </div>
                          <div className="timeline-content">
                            <div className="log-message">{log.title || 'Action Performed'}</div>
                            {/* <div className="log-details">{log.message}</div> */}
                          </div>
                          <div className="timeline-time">
                            <div>
                              {activityLog.date} | {log.time}
                            </div>
                          </div>
                        </div>
                      ))}
                    </React.Fragment>
                  ))}

                {!loggingLoading && userActiveLogs.length === 0 && (
                  <div
                    style={{
                      display: 'flex',
                      padding: '20px',
                      justifyContent: 'center',
                      color: '#666',
                    }}>
                    {t('noActivitylog')}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {editMode && (
          <div
            className="footer-actions"
            style={{ display: 'flex', gap: '15px', marginTop: '20px' }}>
            <MyButton
              label={t('cancel')}
              buttonType="outline" // outline button style
              onClickFunc={toggleEditMode}
            />
            <MyButton
              label={t('save')}
              buttonType="submit"
              onClickFunc={handleEditSubmit}
              disableButton={loading}
              loading={loading}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default UserDetailsSidePanel;
